import { LoadingScreen } from "./LoadingScreen.jsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { AppContext, read, presets, eligible, Modal, Icon } from "./common.jsx";
import { enableAnalytics } from "./analytics.js";
enableAnalytics();
import { Header, Explorer } from "./Explorer.jsx";
import { Packs } from "./Packs.jsx";
import { Converter } from "./Converter.jsx";
import { Studio } from "./Studio.jsx";
import { Detail, Compare, Embed } from "./FontDialogs.jsx";
import { Help } from "./Help.jsx";
import { Tour } from "./Tour.jsx";
import { Tooltips } from "./Tooltips.jsx";
function CompareTray({ ids, clear, open }) {
  const [pos, setPos] = useState(null),
    ref = useRef(null),
    drag = useRef(null);
  const clamp = (x, y) => {
    const r = ref.current.getBoundingClientRect();
    setPos({
      left: Math.max(8, Math.min(x, innerWidth - r.width - 8)),
      top: Math.max(8, Math.min(y, innerHeight - r.height - 8)),
    });
  };
  useEffect(() => {
    const onResize = () => {
      if (pos) clamp(pos.left, pos.top);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [pos]);
  if (!ids.length) return null;
  return (
    <div
      className="comparetray"
      ref={ref}
      style={pos ? { ...pos, bottom: "auto", transform: "none" } : undefined}
    >
      <button
        className="trayhandle"
        aria-label="Move comparison bar"
        onPointerDown={(e) => {
          const r = ref.current.getBoundingClientRect();
          drag.current = {
            x: e.clientX,
            y: e.clientY,
            left: r.left,
            top: r.top,
          };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          if (drag.current) {
            const d = drag.current;
            clamp(d.left + e.clientX - d.x, d.top + e.clientY - d.y);
          }
        }}
        onPointerUp={() => (drag.current = null)}
        onPointerCancel={() => (drag.current = null)}
        onKeyDown={(e) => {
          if (!e.key.startsWith("Arrow")) return;
          e.preventDefault();
          const r = ref.current.getBoundingClientRect(),
            d = e.shiftKey ? 40 : 10;
          clamp(
            r.left +
              (e.key === "ArrowRight" ? d : e.key === "ArrowLeft" ? -d : 0),
            r.top + (e.key === "ArrowDown" ? d : e.key === "ArrowUp" ? -d : 0),
          );
        }}
      >
        <Icon name="grip" />
      </button>
      <span>
        <b>{ids.length} fonts selected</b>
        <small> · Compare up to 4</small>
      </span>
      <button
        data-action="clearcompare"
        aria-label="Clear comparison"
        onClick={clear}
      >
        <Icon name="close" />
      </button>
      <button className="primary" data-action="compare" onClick={open}>
        Compare fonts
      </button>
    </div>
  );
}
function App() {
  const [fonts, setFonts] = useState([]),
    [error, setError] = useState(""),
    [route, setRoute] = useState(
      ["explore", "saved", "converter", "help", "packs"].includes(location.hash.slice(1))
        ? location.hash.slice(1)
        : "explore",
    ),
    [modal, setModal] = useState(null),
    [lang, setLang] = useState(read("anbani-language", "ka")),
    [dark, setDark] = useState(read("anbani-theme", false)),
    [saved, setSaved] = useState(read("anbani-favorites", [])),
    [compare, setCompare] = useState([]),
    [embed, setEmbed] = useState([]),
    [sample, setSample] = useState(presets.rustaveli[1]),
    [preset, setPreset] = useState("rustaveli"),
    [size, setSize] = useState(36),
    [color, setColor] = useState(""),
    [query, setQuery] = useState(""),
    [filters, setFilters] = useState({}),
    [filtersOpen, setFiltersOpen] = useState(false),
    [sidebarCollapsed, setSidebarCollapsed] = useState(read("fontlab-sidebar-collapsed", false)),
    [context, setContext] = useState("newspaper"),
    [headline, setHeadline] = useState(""),
    [autoConvert, setAutoConvert] = useState(
      read("fontlab-auto-convert", true),
    ),
    [tour, setTour] = useState(false),
    [message, setMessage] = useState("");
  const timer = useRef(null);
  useEffect(() => {
    try { localStorage.setItem("fontlab-sidebar-collapsed", JSON.stringify(sidebarCollapsed)); } catch {}
  }, [sidebarCollapsed]);
  const toast = useCallback((text) => {
    setMessage(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setMessage(""), 2700);
  }, []);
  const close = () => {
    setModal(null);
    if (location.hash === "#studio")
      history.replaceState(null, "", "#" + route);
  };
  const open = (type, id) => {
    if (
      type === "studio" &&
      fonts.find((f) => f.id === id)?.previewUnsupported
    ) {
      toast("Browser preview unavailable for this original file");
      return;
    }
    setModal({ type, id });
  };
  const addEmbed = (id) => {
    if (!fonts.some((f) => f.id === id && eligible(f))) return;
    if (!embed.includes(id)) {
      if (embed.length >= 20) {
        toast("You can embed up to 20 fonts at once");
        return;
      }
      setEmbed([...embed, id]);
    }
    setModal({ type: "embed" });
    toast(
      lang === "ka"
        ? "შრიფტი დაემატა ნაკრებს"
        : "Font added to your embed selection",
    );
  };
  const startTour = () => {
    setModal(null);
    setQuery("");
    setFilters({});
    setRoute("explore");
    location.hash = "explore";
    setTour(true);
  };
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    fetch("catalog.json", { signal: controller.signal })
      .then((r) => {
        if (!r.ok) throw Error("Font library could not load");
        return r.json();
      })
      .then((f) => {
        if (!Array.isArray(f) || !f.length) throw Error("The font catalog is unavailable. Please try again.");
        if (active) {
          setFonts(f);
          if (location.hash === "#studio")
            setModal({ type: "studio", id: "905" });
        }
      })
      .catch((e) => { if (active) setError(e.name === "AbortError" ? "The font library took too long to respond. Please try again." : e.message); })
      .finally(() => clearTimeout(timeout));
    return () => {
      active = false;
      clearTimeout(timeout);
      controller.abort();
    };
  }, []);
  useEffect(() => {
    const change = () => {
      const key = location.hash.slice(1);
      if (key === "studio") {
        setModal({ type: "studio", id: "905" });
        return;
      }
      setModal(null);
      setRoute(
        ["explore", "saved", "converter", "help", "packs"].includes(key)
          ? key
          : "explore",
      );
      window.scrollTo(0, 0);
    };
    window.addEventListener("hashchange", change);
    return () => window.removeEventListener("hashchange", change);
  }, []);
  useEffect(() => {
    document.documentElement.lang = lang;
    document.body.classList.toggle("dark", dark);
    document.body.classList.toggle(
      "explorer-view",
      route === "explore" || route === "saved" || route === "packs",
    );
    try {
      localStorage.setItem("anbani-language", JSON.stringify(lang));
      localStorage.setItem("anbani-theme", JSON.stringify(dark));
      localStorage.setItem("anbani-favorites", JSON.stringify(saved));
      localStorage.setItem("fontlab-auto-convert", JSON.stringify(autoConvert));
    } catch {}
  }, [lang, dark, saved, route, autoConvert]);
  useEffect(() => {
    const key = (e) => {
      if (
        e.key === "/" &&
        !["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName) &&
        !modal
      ) {
        const search = document.querySelector("#search");
        if (search) {
          e.preventDefault();
          search.focus();
        }
      }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [modal]);
  useEffect(() => {
    if (!fonts.length || !document.modelContext?.registerTool) return;
    try {
      document.modelContext.registerTool({
        name: "search_georgian_fonts",
        title: "Search Georgian fonts",
        description: "Search the visible Georgian font library.",
        inputSchema: {
          type: "object",
          properties: { query: { type: "string" } },
          required: ["query"],
          additionalProperties: false,
        },
        annotations: { readOnlyHint: false },
        execute: ({ query }) => {
          if (typeof query !== "string")
            throw new TypeError("query must be text");
          setQuery(query);
          setFilters({});
          setModal(null);
          setRoute("explore");
          history.replaceState(null, "", "#explore");
          const matches = fonts.filter((f) =>
            (f.name + " " + f.authors.join(" "))
              .toLowerCase()
              .includes(query.toLowerCase()),
          );
          return {
            count: matches.length,
            fonts: matches
              .slice(0, 12)
              .map((f) => ({ id: f.id, name: f.name })),
          };
        },
      });
    } catch {}
    return () => {
      try {
        document.modelContext?.unregisterTool?.("search_georgian_fonts");
      } catch {}
    };
  }, [fonts]);
  const value = {
    fonts,
    route,
    modal,
    open,
    close,
    lang,
    setLang,
    dark,
    setDark,
    saved,
    compare,
    embed,
    setEmbed,
    addEmbed,
    sample,
    setSample,
    preset,
    setPreset,
    size,
    setSize,
    color,
    setColor,
    query,
    setQuery,
    filters,
    setFilters,
    filtersOpen,
    setFiltersOpen,
    sidebarCollapsed,
    setSidebarCollapsed,
    context,
    setContext,
    headline,
    setHeadline,
    autoConvert,
    setAutoConvert,
    tour,
    setTour,
    startTour,
    toast,
    toggleSaved: (id) =>
      setSaved(
        saved.includes(id) ? saved.filter((x) => x !== id) : [...saved, id],
      ),
    toggleCompare: (id) => {
      if (compare.includes(id)) setCompare(compare.filter((x) => x !== id));
      else if (compare.length < 4) setCompare([...compare, id]);
      else toast("Compare up to 4 fonts");
    },
    copy: async (text) => {
      try {
        await navigator.clipboard.writeText(text);
        toast(lang === "ka" ? "დაკოპირებულია" : "Copied to clipboard");
      } catch {
        toast("Copy unavailable. Select and copy the code manually.");
      }
    },
  };
  if (error)
    return (
      <main className="wide">
        <h1>{error}</h1>
        <button onClick={() => location.reload()}>Try again</button>
      </main>
    );
  if (!fonts.length)
    return <LoadingScreen />;
  return (
    <AppContext.Provider value={value}>
      <Header />
      {route === "packs" ? (<Packs />) : route === "converter" ? (
        <Converter />
      ) : route === "help" ? (
        <Help />
      ) : (
        <Explorer />
      )}
      <CompareTray
        ids={compare}
        clear={() => setCompare([])}
        open={() => open("compare")}
      />
      {modal && (
        <Modal close={close}>
          {modal.type === "studio" ? (
            <Studio />
          ) : modal.type === "embed" ? (
            <Embed />
          ) : modal.type === "compare" ? (
            <Compare />
          ) : (
            <Detail key={modal.id} />
          )}
        </Modal>
      )}
      <div id="toast" role="status" className={message ? "show" : ""}>
        {message}
      </div>
      {tour && <Tour />}
      <Tooltips />
    </AppContext.Provider>
  );
}
createRoot(document.getElementById("app")).render(<App />);
